<script setup lang="ts">
import { computed } from 'vue';
import { useWindowSize } from '@vueuse/core';
import type { DialogProps } from '../../organisms';
import { DATA_TABLE_SIZE, useDataTableFilters } from '@DataTableModule';
import { useActiveFilter, useSavedReports } from '@FilterModule';

defineEmits<{
  onApplyFilters: [];
  onApplySavedReport: [];
  onClearFilters: [];
  dateRangeChanged: [{ startDate: string; endDate: string }];
}>();

const props = withDefaults(
  defineProps<{
    adminColumns?: string[];
    adminColumnsVisible?: string[];
    dialog?: DialogProps;
    exceptionFields?: string[];
    hasCustomFieldFilters?: boolean;
    hasDataTableFilters?: boolean;
    hasFilters?: boolean;
    hasSearchInput?: boolean;
    hasShowHideColumns?: boolean;
    instanceComposable?: any;
    loading?: boolean;
    savedReportsTableId?: string;
    showAllFilters?: boolean;
    sortByLabel?: boolean;
  }>(),
  {
    hasCustomFieldFilters: false,
    hasFilters: true,
    hasSearchInput: true,
    hasShowHideColumns: true,
    exceptionFields: () => [],
    showAllFilters: false,
    sortByLabel: false,
  }
);

const filtersInstance = props.instanceComposable ? props.instanceComposable() : useDataTableFilters();

const { height, width } = useWindowSize();
const { activeFilters } = useActiveFilter();
const { isSavedReportsEnabled } = useSavedReports();

const { searchQuery, size } = filtersInstance;

const hasColumnPicker = computed(() => !!props.hasDataTableFilters && props.hasShowHideColumns);
const hasFiltersColumnsButton = computed(
  () => (!!activeFilters.value?.length && props.hasFilters) || hasColumnPicker.value
);
const hasSavedReports = computed(() => !!props.savedReportsTableId && isSavedReportsEnabled.value);
</script>

<template>
  <Accordion
    v-if="width < 768 || height < 576"
    class="my-3 rounded-md border"
    pt:content="!pb-0 !px-2"
    pt:headeraction="!p-2 !bg-gray-100"
  >
    <AccordionTab>
      <template #header>
        <span class="flex w-full items-center gap-2">
          <i class="pi pi-filter"></i>

          <span class="text-sm font-medium text-gray-600">
            {{ $t('filters.filtersAndActions') }}
          </span>
        </span>
      </template>

      <div class="my-4 flex flex-wrap justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex flex-wrap gap-2">
            <MSearchInput
              v-if="hasSearchInput"
              v-model="searchQuery"
              :loading
            />

            <OFiltersColumns
              v-if="hasFiltersColumnsButton"
              :adminColumns
              :adminColumnsVisible
              :dialog
              :exceptionFields
              :filters="activeFilters"
              :hasColumns="hasColumnPicker"
              :hasCustomFieldFilters
              :loading
              :savedReportsTableId
              :showAllFilters
              :sortByLabel
              @onApplyFilters="$emit('onApplyFilters')"
              @onClearFilters="$emit('onClearFilters')"
            />

            <OSavedReports
              v-if="hasSavedReports && savedReportsTableId"
              :dataTableId="savedReportsTableId"
              :loading
              @onApplySavedReport="$emit('onApplySavedReport')"
            />

            <slot name="slotLeft" />
          </div>
        </div>

        <div class="flex flex-wrap gap-2 border-t pt-2 md:border-none md:pt-0">
          <slot />

          <div
            v-if="hasDataTableFilters"
            class="flex gap-2"
          >
            <MButtonDropdown
              v-model="size"
              tooltip="components.dataTable.tableSizeDropdown.tooltip"
              :items="DATA_TABLE_SIZE"
              :loading
            />
          </div>

          <slot name="slotRight" />
        </div>
      </div>
    </AccordionTab>
  </Accordion>

  <div
    v-else
    class="my-4 flex flex-wrap justify-between gap-2"
  >
    <div class="flex flex-wrap items-center gap-2">
      <div class="flex flex-wrap gap-2">
        <MSearchInput
          v-if="hasSearchInput"
          v-model="searchQuery"
          :loading
        />

        <OFiltersColumns
          v-if="hasFiltersColumnsButton"
          :adminColumns
          :adminColumnsVisible
          :dialog
          :exceptionFields
          :filters="activeFilters"
          :hasColumns="hasColumnPicker"
          :hasCustomFieldFilters
          :loading
          :savedReportsTableId
          :showAllFilters
          :sortByLabel
          @onApplyFilters="$emit('onApplyFilters')"
          @onClearFilters="$emit('onClearFilters')"
        />

        <OSavedReports
          v-if="hasSavedReports && savedReportsTableId"
          :dataTableId="savedReportsTableId"
          :loading
          @onApplySavedReport="$emit('onApplySavedReport')"
        />

        <slot name="slotLeft" />
      </div>
    </div>

    <div class="flex flex-wrap gap-2">
      <slot />

      <div
        v-if="hasDataTableFilters"
        class="flex flex-wrap gap-2"
      >
        <MButtonDropdown
          v-model="size"
          tooltip="components.dataTable.tableSizeDropdown.tooltip"
          :items="DATA_TABLE_SIZE"
          :loading
        />
      </div>

      <slot name="slotRight" />
    </div>
  </div>
</template>
